import * as React from 'react';
import { createStyles, Title, Text, Button, Container, Group } from '@mantine/core';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Contexts
import { AuthContext } from '../../../contexts/Auth';

// Hooks
import { usePermission } from '../../../hooks/usePermission';

// Types
import { IUserRole } from '../../../types';

const useStyles = createStyles((theme) => ({
    root: {
        paddingTop: 80,
        paddingBottom: 80
    },

    label: {
        textAlign: 'center',
        fontWeight: 900,
        fontSize: 220,
        lineHeight: 1,
        marginBottom: theme.spacing.xl,
        color: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],

        [theme.fn.smallerThan('sm')]: {
            fontSize: 120
        }
    },

    title: {
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        textAlign: 'center',
        fontWeight: 900,
        fontSize: 38,

        [theme.fn.smallerThan('sm')]: {
            fontSize: 32
        }
    },

    description: {
        maxWidth: 500,
        margin: 'auto',
        marginTop: theme.spacing.xl,
        marginBottom: theme.spacing.xl
    }
}));

const contentByCode: Record<
    string,
    Record<string, { title: string; description: string; linkTo?: string; buttonLabel?: string }>
> = {
    '404': {
        default: {
            title: 'Not found.',
            description:
                'Unfortunately, this is only a 404 page. You may have mistyped the address, or the page has been moved to another URL.'
        }
    },
    '401': {
        cic: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, the account with which you are logged in does not have permission to access this page. Please click the link below to return to the CIC dashboard.',
            linkTo: '/admin',
            buttonLabel: 'returnToDashboard'
        },
        client: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, the account with which you are logged in does not have permission to access this page. Please click the link below to return to the client dashboard.',
            linkTo: '/client',
            buttonLabel: 'returnToDashboard'
        },
        default: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, you are not authorized to access this page. If you have an account with the required permission, please click here to login with the credentials for that account.',
            linkTo: '/logout',
            buttonLabel: 'signIn'
        }
    },
    '403': {
        cic: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, the account with which you are logged in does not have permission to access this page. Please click the link below to return to the CIC dashboard.',
            linkTo: '/admin',
            buttonLabel: 'returnToDashboard'
        },
        client: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, the account with which you are logged in does not have permission to access this page. Please click the link below to return to the client dashboard.',
            linkTo: '/client',
            buttonLabel: 'returnToDashboard'
        },
        default: {
            title: 'Unathorized request.',
            description:
                'Unfortunately, you are not authorized to access this page. If you have an account with the required permission, please click here to login with the credentials for that account.',
            linkTo: '/logout',
            buttonLabel: 'signIn'
        }
    },
    '500': {
        default: {
            title: 'Internal server error.',
            description: 'Unfortunately, there was a server error while processing your request.'
        }
    },
    fallback: {
        cic: {
            title: 'Unspecified Error',
            description: 'Unfortunately, there was an unspecified error at this route.',
            linkTo: '/admin',
            buttonLabel: 'returnToDashboard'
        },
        client: {
            title: 'Unspecified Error',
            description: 'Unfortunately, there was an unspecified error at this route.',
            linkTo: '/client',
            buttonLabel: 'returnToDashboard'
        },
        default: {
            title: 'Unspecified Error',
            description: 'Unfortunately, there was an unspecified error at this route.'
        }
    }
};

export const Error: React.FC = () => {
    const isCicUser = usePermission([IUserRole.CIC_ADMIN, IUserRole.CIC_OWNER, IUserRole.CIC_USER]);
    const isClientUser = usePermission([IUserRole.ORG_USER, IUserRole.ORG_PRIMARY]);
    const { errorCode } = useParams();
    const { t } = useTranslation('common');
    const { classes } = useStyles();
    const authType = React.useMemo(() => {
        if (isCicUser) {
            return 'cic';
        } else if (isClientUser) {
            return 'client';
        } else {
            return 'default';
        }
    }, [isCicUser, isClientUser]);
    const content = React.useMemo(() => {
        return errorCode
            ? contentByCode[errorCode]?.[authType] ??
                  contentByCode.fallback?.[authType] ??
                  contentByCode.fallback.default
            : contentByCode.fallback.default;
    }, [errorCode, authType]);

    return (
        <Container className={classes.root}>
            <div className={classes.label}>{errorCode}</div>
            <Title className={classes.title}>{content.title}</Title>
            <Text color="dimmed" size="lg" align="center" className={classes.description}>
                {content.description}
            </Text>
            <Group position="center">
                <Link to={content?.linkTo ?? '/'}>
                    <Button variant="outline" size="md">
                        {content?.buttonLabel ? t(content.buttonLabel) : t('returnHome')}
                    </Button>
                </Link>
            </Group>
        </Container>
    );
};

export default Error;
